// cSpell:ignore Silae SILAE rubrique cumul cumulstockvar mtpart questionnaireVariable advancedVariable complexExpression
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSilaeVariables } from "@/hooks/useSilaeVariables";
import type {
  FormatterContext,
  SilaeFormatterFormatSourceDisplayOptions,
  SilaeFormatters,
} from "@/types/silae-formatters";
import type { SilaeSourceFormData } from "@/types/silae";
import {
  findRubriqueLabel,
  formatAdvancedVariable,
  formatBonusBloc52,
  formatBonusCounterType,
  formatBonusDsnDateRange,
  formatBonusMemoLines,
  formatBonusValueType,
  formatCalculMethod,
  formatComplexExpression,
  formatCondition,
  formatCumulBulletins,
  formatCumulStockVar,
  formatPredefinedVariableLabel,
  formatQuestionnaireVariable,
  formatSource,
  resolveVariableLabel,
} from "@/utils/silae-formatters";

export type { SilaeFormatters } from "@/types/silae-formatters";

export function useSilaeFormatters(): SilaeFormatters {
  const { t } = useTranslation();
  const { findVariable } = useSilaeVariables();

  const ctx = useMemo<FormatterContext>(
    () => ({ t, findVariable }),
    [t, findVariable],
  );

  return useMemo<SilaeFormatters>(
    () => ({
      formatSourceDisplay: (
        source: SilaeSourceFormData,
        options?: SilaeFormatterFormatSourceDisplayOptions,
      ) =>
        formatSource(source, ctx, {
          formatComplex: formatComplexExpression,
          skipEvpForce: options?.skipEvpForce,
        }),
      formatComplexExpressionDisplay: (expression) =>
        formatComplexExpression(expression, ctx),
      formatCumulBulletinsDisplay: (
        cumulPeriod,
        cumulType,
        customStartDate,
        customEndDate,
        cumulDivisor,
      ) =>
        formatCumulBulletins(
          cumulPeriod,
          cumulType,
          customStartDate,
          customEndDate,
          cumulDivisor,
          ctx,
        ),
      formatCumulStockVarDisplay: (
        name,
        dateDebut,
        dateFin,
        percentage,
        enableAdditional,
        additionalOperator,
        additionalValue,
        period,
      ) =>
        formatCumulStockVar(
          name,
          dateDebut,
          dateFin,
          percentage,
          enableAdditional,
          additionalOperator,
          additionalValue,
          ctx,
          period,
        ),
      formatQuestionnaireVariableDisplay: (type, index, label, percentage) =>
        formatQuestionnaireVariable(type, index, label, percentage, ctx),
      formatAdvancedVariableDisplay: (
        variableCode,
        parameters,
        percentage,
        globalEnableAdditional,
        globalAdditionalOperator,
        globalAdditionalValue,
      ) =>
        formatAdvancedVariable(
          variableCode,
          parameters,
          percentage,
          globalEnableAdditional,
          globalAdditionalOperator,
          globalAdditionalValue,
          ctx,
        ),
      formatConditionDescription: (condition) =>
        formatCondition(condition, ctx),
      findRubriqueLabel,
      formatCalculMethod: (calculMethod) =>
        formatCalculMethod(calculMethod, ctx),
      formatPredefinedVariableLabel: (conditionType) =>
        formatPredefinedVariableLabel(conditionType, ctx),
      formatBonusCounterType: (code) => formatBonusCounterType(code, ctx),
      formatBonusValueType: (type) => formatBonusValueType(type, ctx),
      formatBonusBloc52: (code, customCode) =>
        formatBonusBloc52(code, customCode, ctx),
      formatBonusDsnDateRange: (start, end) =>
        formatBonusDsnDateRange(start, end, ctx),
      formatBonusMemoLines: (raw) => formatBonusMemoLines(raw),
      resolveSilaeVariableLabel: (code) =>
        resolveVariableLabel(findVariable(code), code),
    }),
    [ctx, findVariable],
  );
}
